/**
 * Databases (Notion-style), as pure data. A database is a page whose schema is
 * stored in the page property `nexus.database`; its rows are its child pages and
 * a row's values are ordinary page properties, keyed by the property name. That
 * keeps rows readable as Markdown frontmatter and compatible with the task logic.
 */
import { FormulaError, runFormula } from './formula';
import { splitDateTime } from './tasks';

export const DATABASE_PROP = 'nexus.database';

export type PropertyType =
  | 'text'
  | 'number'
  | 'select'
  | 'multi_select'
  | 'status'
  | 'date'
  /** A moment ("2026-10-05T09:00") or a time before the due date ("1d", "30m"). */
  | 'reminder'
  | 'checkbox'
  | 'person'
  | 'url'
  | 'email'
  | 'phone'
  | 'page'
  /** Links to rows of another database, stored as `[[Title]]` (like `page`, several). */
  | 'relation'
  /** Computed: rows of another database whose relation/page property points here. */
  | 'backlink'
  /** Computed: a summary over a relation/backlink (count, sum, % done, …). */
  | 'rollup'
  /** Computed: an expression over the row's values, e.g. days left. */
  | 'formula'
  /** An action on the row (e.g. "next occurrence" of a recurring task). */
  | 'button';

/** Types whose value is computed, never stored on the row. */
export const COMPUTED_TYPES: PropertyType[] = ['backlink', 'rollup', 'formula', 'button'];
export const isComputed = (def: Pick<PropertyDef, 'type'>) => COMPUTED_TYPES.includes(def.type);

export type RollupFn =
  | 'count'
  | 'count_values'
  | 'sum'
  | 'average'
  | 'min'
  | 'max'
  | 'earliest'
  | 'latest'
  | 'percent_checked'
  | 'percent_matching'
  | 'show';

export const ROLLUP_FNS: RollupFn[] = [
  'count',
  'count_values',
  'sum',
  'average',
  'min',
  'max',
  'earliest',
  'latest',
  'percent_checked',
  'percent_matching',
  'show',
];

export const PROPERTY_TYPES: PropertyType[] = [
  'text',
  'number',
  'select',
  'multi_select',
  'status',
  'date',
  'reminder',
  'checkbox',
  'person',
  'url',
  'email',
  'phone',
  'page',
  'relation',
  'backlink',
  'rollup',
  'formula',
  'button',
];

export type OptionColor =
  'gray' | 'brown' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple' | 'pink' | 'red';

export const OPTION_COLORS: OptionColor[] = [
  'gray',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
];

export interface SelectOption {
  name: string;
  color: OptionColor;
}

export interface PropertyDef {
  id: string;
  /** Shown name. */
  name: string;
  /**
   * Page property keys holding the value (case-insensitive), first one used for
   * new values. Missing → the name. Lets "Deadline" store under `due`, and a
   * renamed property keep reading its old key.
   */
  keys?: string[];
  type: PropertyType;
  options?: SelectOption[];
  /** Section in the row's property panel, e.g. "Overview". */
  group?: string;
  /** relation: the database whose rows can be linked. */
  relation?: { databaseId: string };
  /** backlink: rows of `databaseId` whose property `propertyId` links to this row. */
  backlink?: { databaseId: string; propertyId: string };
  /**
   * rollup: over the rows of relation/backlink `via`, summarise `target` (a
   * property id there, or the title). `percent_matching` counts rows where the
   * filter matches (e.g. Status is Done).
   */
  rollup?: { via: string; target: string; fn: RollupFn; match?: Omit<Filter, 'propId'> };
  /** formula: e.g. `days_until(prop("Deadline"))`. */
  formula?: { expression: string };
  /** button: what it does. */
  button?: { action: 'recur' };
}

/** Types whose values are links to pages ("[[Title]]"). */
export const LINK_TYPES: PropertyType[] = ['page', 'relation', 'backlink'];

/** Marks a row as a template of its database (hidden from views and task lists). */
export const TEMPLATE_PROP = 'nexus.template';

/** The page title acts as the first column. */
export const TITLE_ID = 'title';

export type FilterOp =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'not_contains'
  | 'empty'
  | 'not_empty'
  | 'before'
  | 'after'
  | 'on_or_before'
  | 'on_or_after'
  | 'gt'
  | 'lt'
  | 'checked'
  | 'unchecked';

export interface Filter {
  propId: string;
  op: FilterOp;
  /** For dates also "today", "today+7", "today-1". */
  value?: string;
}

export interface Sort {
  propId: string;
  dir: 'asc' | 'desc';
}

export type ViewType = 'table' | 'board' | 'list' | 'calendar' | 'gallery';

export interface ViewDef {
  id: string;
  name: string;
  type: ViewType;
  filters: Filter[];
  sorts: Sort[];
  /** Board: select/status property whose options become columns. */
  groupBy?: string;
  /** Calendar: the date property that places rows on days. */
  dateBy?: string;
  /** Visible properties in order (title is always first); missing → all. */
  columns?: string[];
  widths?: Record<string, number>;
}

export interface DatabaseSchema {
  version: 1;
  properties: PropertyDef[];
  views: ViewDef[];
  /** Properties set on new rows that are not columns, e.g. `{ type: 'project' }`. */
  rowDefaults?: Record<string, unknown>;
}

let counter = 0;
/** Short id, unique enough within one schema. */
export function shortId(): string {
  counter = (counter + 1) % 1296;
  return (Date.now().toString(36).slice(-5) + counter.toString(36)).padStart(6, '0');
}

/** The schema stored on a page, or null when the page is not a database. */
export function readSchema(props: Record<string, unknown>): DatabaseSchema | null {
  const raw = props[DATABASE_PROP];
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<DatabaseSchema>;
  if (!Array.isArray(s.properties)) return null;
  const views = Array.isArray(s.views) && s.views.length ? s.views : [defaultView()];
  return {
    version: 1,
    properties: s.properties.filter((p) => p && typeof p.name === 'string'),
    views: views.map((v) => ({ ...v, filters: v.filters ?? [], sorts: v.sorts ?? [] })),
    ...(s.rowDefaults ? { rowDefaults: s.rowDefaults } : {}),
  };
}

export const defaultView = (name = 'Tabel', type: ViewType = 'table'): ViewDef => ({
  id: shortId(),
  name,
  type,
  filters: [],
  sorts: [],
});

// ---- values --------------------------------------------------------------------------

const lower = (s: string) => s.toLowerCase();

const keysOf = (def: Pick<PropertyDef, 'name' | 'keys'>) =>
  def.keys?.length ? def.keys : [def.name];

/** The row's existing key for a property (any of its keys, any case), if set. */
function existingKey(
  props: Record<string, unknown>,
  def: Pick<PropertyDef, 'name' | 'keys'>,
): string | undefined {
  for (const key of keysOf(def)) {
    const hit = Object.keys(props).find((k) => lower(k) === lower(key) && !isEmptyValue(props[k]));
    if (hit !== undefined) return hit;
  }
  for (const key of keysOf(def)) {
    const hit = Object.keys(props).find((k) => lower(k) === lower(key));
    if (hit !== undefined) return hit;
  }
  return undefined;
}

/** A row's raw value for a property. */
export function rawValue(
  props: Record<string, unknown>,
  def: Pick<PropertyDef, 'name' | 'keys'>,
): unknown {
  const key = existingKey(props, def);
  return key === undefined ? undefined : props[key];
}

/** The key to write a value under: the row's existing key, else the property's first key. */
export function valueKey(
  props: Record<string, unknown>,
  def: Pick<PropertyDef, 'name' | 'keys'>,
): string {
  return existingKey(props, def) ?? keysOf(def)[0]!;
}

/** Does a page property key belong to one of the schema's properties? */
export function isSchemaKey(schema: DatabaseSchema, key: string): boolean {
  return schema.properties.some((p) => keysOf(p).some((k) => lower(k) === lower(key)));
}

export const isEmptyValue = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** Values as a list (multi-select, person; a single value becomes one item). */
export function asList(v: unknown): string[] {
  if (isEmptyValue(v)) return [];
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  return String(v)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** "[[Alpha|A]]" → "Alpha". */
export const linkTarget = (v: string) =>
  v
    .replace(/^\[\[|\]\]$/g, '')
    .split('|')[0]!
    .trim();

/** Text for searching, sorting and comparing. */
export function textOf(def: PropertyDef | undefined, v: unknown): string {
  if (isEmptyValue(v)) return '';
  if (def && LINK_TYPES.includes(def.type)) return asList(v).map(linkTarget).join(', ');
  if (Array.isArray(v)) return v.map(String).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Today (local) as YYYY-MM-DD, or "today+N" relative. */
function resolveDate(value: string, today: string): string {
  const m = /^today([+-]\d+)?$/.exec(value.trim());
  if (!m) return value.trim().slice(0, 10);
  const d = new Date(`${today}T12:00:00`);
  d.setDate(d.getDate() + Number(m[1] ?? 0));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ---- filters & sorts ---------------------------------------------------------------------

export interface RowLike {
  title: string;
  props: Record<string, unknown>;
  /** Values of computed properties (backlink, rollup, formula), by property id. */
  computed?: Record<string, unknown>;
}

/** A row's value for a property: stored, or computed. */
export function cellValue(def: PropertyDef, row: RowLike): unknown {
  return isComputed(def) ? row.computed?.[def.id] : rawValue(row.props, def);
}

function valueFor(schema: DatabaseSchema, row: RowLike, propId: string) {
  if (propId === TITLE_ID) return { def: undefined, value: row.title as unknown };
  const def = schema.properties.find((p) => p.id === propId);
  return { def, value: def ? cellValue(def, row) : undefined };
}

export function matchesFilter(
  schema: DatabaseSchema,
  row: RowLike,
  f: Filter,
  today: string,
): boolean {
  const { def, value } = valueFor(schema, row, f.propId);
  if (f.propId !== TITLE_ID && !def) return true; // filter on a deleted property: ignore
  const want = (f.value ?? '').trim();
  const text = lower(textOf(def, value));
  switch (f.op) {
    case 'empty':
      return isEmptyValue(value);
    case 'not_empty':
      return !isEmptyValue(value);
    case 'checked':
      return value === true || value === 'true';
    case 'unchecked':
      return !(value === true || value === 'true');
    case 'is':
    case 'is_not': {
      const list =
        def && ['multi_select', 'person', ...LINK_TYPES].includes(def.type)
          ? asList(value).map((x) => lower(LINK_TYPES.includes(def.type) ? linkTarget(x) : x))
          : [text];
      const hit = list.includes(lower(want));
      return f.op === 'is' ? hit : !hit;
    }
    case 'contains':
      return text.includes(lower(want));
    case 'not_contains':
      return !text.includes(lower(want));
    case 'gt':
    case 'lt': {
      const n = Number(value);
      if (isEmptyValue(value) || Number.isNaN(n)) return false;
      return f.op === 'gt' ? n > Number(want) : n < Number(want);
    }
    default: {
      // Dates: compare the day (a time on the value does not matter).
      const day = typeof value === 'string' ? splitDateTime(value)?.date : undefined;
      if (!day || !want) return false;
      const ref = resolveDate(want, today);
      if (f.op === 'before') return day < ref;
      if (f.op === 'after') return day > ref;
      if (f.op === 'on_or_before') return day <= ref;
      return day >= ref;
    }
  }
}

/** Rank of a select/status value: its option order, unknown values last. */
function optionRank(def: PropertyDef, v: unknown): number {
  const i = (def.options ?? []).findIndex((o) => lower(o.name) === lower(String(v ?? '')));
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

export function compareRows(schema: DatabaseSchema, a: RowLike, b: RowLike, sorts: Sort[]): number {
  for (const s of sorts) {
    const va = valueFor(schema, a, s.propId);
    const vb = valueFor(schema, b, s.propId);
    const ea = isEmptyValue(va.value);
    const eb = isEmptyValue(vb.value);
    if (ea !== eb) return ea ? 1 : -1; // empty values last, whatever the direction
    if (ea && eb) continue;
    let c: number;
    const def = va.def;
    if (def && (def.type === 'select' || def.type === 'status')) {
      c = optionRank(def, va.value) - optionRank(def, vb.value);
    } else if (def?.type === 'number') {
      c = Number(va.value) - Number(vb.value);
    } else if (def?.type === 'checkbox') {
      c = Number(va.value === true) - Number(vb.value === true);
    } else {
      c = textOf(def, va.value).localeCompare(textOf(def, vb.value), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
    if (c !== 0) return s.dir === 'asc' ? c : -c;
  }
  return 0;
}

/** Rows of a view: filtered (all filters must match) and sorted. */
export function applyView<R extends RowLike>(
  schema: DatabaseSchema,
  view: ViewDef,
  rows: R[],
  today: string,
): R[] {
  const kept = rows.filter((r) => view.filters.every((f) => matchesFilter(schema, r, f, today)));
  return view.sorts.length ? kept.sort((a, b) => compareRows(schema, a, b, view.sorts)) : kept;
}

/** Visible properties of a view, in order. */
export function viewColumns(schema: DatabaseSchema, view: ViewDef): PropertyDef[] {
  if (!view.columns) return schema.properties;
  const byId = new Map(schema.properties.map((p) => [p.id, p]));
  return view.columns.map((id) => byId.get(id)).filter((p): p is PropertyDef => !!p);
}

// ---- schema edits (pure) ---------------------------------------------------------------

export function nextOptionColor(options: SelectOption[] = []): OptionColor {
  return OPTION_COLORS[(options.length + 1) % OPTION_COLORS.length]!;
}

/** Add an option (when it is not there yet, case-insensitive). */
export function withOption(def: PropertyDef, name: string): PropertyDef {
  const options = def.options ?? [];
  if (!name.trim() || options.some((o) => lower(o.name) === lower(name.trim()))) return def;
  return { ...def, options: [...options, { name: name.trim(), color: nextOptionColor(options) }] };
}

export function optionOf(def: PropertyDef, value: unknown): SelectOption | undefined {
  return def.options?.find((o) => lower(o.name) === lower(String(value ?? '')));
}

// ---- starter templates ------------------------------------------------------------------

export type DatabaseTemplate = 'empty' | 'tasks' | 'projects' | 'meetings';

const prop = (
  name: string,
  type: PropertyType,
  keys: string[],
  extra: Partial<PropertyDef> = {},
): PropertyDef => ({ id: shortId(), name, keys, type, ...extra });
const opts = (...pairs: [string, OptionColor][]): SelectOption[] =>
  pairs.map(([name, color]) => ({ name, color }));
const view = (name: string, type: ViewType, extra: Partial<ViewDef> = {}): ViewDef => ({
  ...defaultView(name, type),
  ...extra,
});

/**
 * Starter schemas (user content, so in Dutch). Values are stored under the keys
 * the rest of Nexus already reads (status, priority, due, project, …), so tasks,
 * reminders and project progress keep working on database rows.
 */
export function templateSchema(template: DatabaseTemplate): DatabaseSchema {
  if (template === 'tasks') {
    const status = prop('Status', 'status', ['status'], {
      group: 'Overzicht',
      // The values the task logic writes (STATUS_WORD), in Notion's colours.
      options: opts(
        ['To do', 'red'],
        ['In progress', 'blue'],
        ['On Hold', 'yellow'],
        ['Done', 'green'],
        ['Cancelled', 'gray'],
      ),
    });
    const due = prop('Deadline', 'date', ['due', 'due date', 'deadline', 'date', 'next due'], {
      group: 'Overzicht',
    });
    const priority = prop('Prioriteit', 'select', ['priority'], {
      group: 'Overzicht',
      options: opts(
        ['Highest', 'red'],
        ['High', 'orange'],
        ['Medium', 'yellow'],
        ['Low', 'blue'],
        ['Lowest', 'gray'],
      ),
    });
    return {
      version: 1,
      properties: [
        status,
        due,
        priority,
        prop('Categorie', 'select', ['category'], { group: 'Overzicht', options: [] }),
        prop('Project', 'page', ['project'], { group: 'Overzicht' }),
        prop('Eigenaar', 'person', ['owner'], { group: 'Details' }),
        prop('Herinnering', 'reminder', ['reminder', 'herinnering'], { group: 'Details' }),
        prop('Tags', 'multi_select', ['tags'], { group: 'Details', options: [] }),
      ],
      views: [
        view('Open', 'table', {
          filters: [
            { propId: status.id, op: 'is_not', value: 'Done' },
            { propId: status.id, op: 'is_not', value: 'Cancelled' },
          ],
          sorts: [
            { propId: due.id, dir: 'asc' },
            { propId: priority.id, dir: 'asc' },
          ],
        }),
        view('Bord', 'board', { groupBy: status.id }),
        view('Alle', 'table'),
      ],
    };
  }
  if (template === 'projects') {
    const status = prop('Status', 'status', ['status'], {
      options: opts(
        ['Idee', 'gray'],
        ['Actief', 'blue'],
        ['On Hold', 'yellow'],
        ['Afgerond', 'green'],
      ),
    });
    const deadline = prop('Deadline', 'date', ['deadline', 'due']);
    return {
      version: 1,
      properties: [
        status,
        deadline,
        prop('Eigenaar', 'person', ['owner']),
        prop('Gebied', 'select', ['area'], { options: [] }),
        prop('Tags', 'multi_select', ['tags'], { options: [] }),
      ],
      views: [
        view('Actief', 'table', {
          filters: [{ propId: status.id, op: 'is', value: 'Actief' }],
          sorts: [{ propId: deadline.id, dir: 'asc' }],
        }),
        view('Bord', 'board', { groupBy: status.id }),
        view('Alle', 'table'),
      ],
      // Rows are projects: they get the project panel with their tasks.
      rowDefaults: { type: 'project' },
    };
  }
  if (template === 'meetings') {
    const date = prop('Datum', 'date', ['date']);
    return {
      version: 1,
      properties: [
        date,
        prop('Soort', 'select', ['kind'], {
          options: opts(
            ['Overleg', 'blue'],
            ['1-op-1', 'purple'],
            ['Klant', 'orange'],
            ['Intern', 'gray'],
          ),
        }),
        prop('Deelnemers', 'person', ['attendees']),
        prop('Project', 'page', ['project']),
        prop('Tags', 'multi_select', ['tags'], { options: [] }),
      ],
      views: [
        view('Komend', 'list', {
          filters: [{ propId: date.id, op: 'on_or_after', value: 'today' }],
          sorts: [{ propId: date.id, dir: 'asc' }],
        }),
        view('Alle', 'table', { sorts: [{ propId: date.id, dir: 'desc' }] }),
      ],
      rowDefaults: { type: 'meeting' },
    };
  }
  return { version: 1, properties: [], views: [view('Tabel', 'table')] };
}

// ---- schema operations (pure; the UI saves the result) ------------------------------------

const SELECT_TYPES: PropertyType[] = ['select', 'status', 'multi_select'];

/** A name not used by another property ("Eigenschap", "Eigenschap 2", …). */
export function uniqueName(schema: DatabaseSchema, base: string): string {
  const taken = new Set(schema.properties.map((p) => lower(p.name)));
  if (!taken.has(lower(base))) return base;
  let n = 2;
  while (taken.has(lower(`${base} ${n}`))) n++;
  return `${base} ${n}`;
}

export function addProperty(
  schema: DatabaseSchema,
  name: string,
  type: PropertyType = 'text',
): { schema: DatabaseSchema; def: PropertyDef } {
  const def: PropertyDef = {
    id: shortId(),
    name: uniqueName(schema, name),
    type,
    ...(SELECT_TYPES.includes(type) ? { options: [] } : {}),
  };
  return {
    def,
    schema: {
      ...schema,
      properties: [...schema.properties, def],
      // Views with an explicit column list show the new property too.
      views: schema.views.map((v) => (v.columns ? { ...v, columns: [...v.columns, def.id] } : v)),
    },
  };
}

export function updateProperty(schema: DatabaseSchema, def: PropertyDef): DatabaseSchema {
  return { ...schema, properties: schema.properties.map((p) => (p.id === def.id ? def : p)) };
}

/** Rename; the values stay where they are (the old name becomes the storage key). */
export function renameProperty(schema: DatabaseSchema, id: string, name: string): DatabaseSchema {
  const def = schema.properties.find((p) => p.id === id);
  const clean = name.trim();
  if (!def || !clean || clean === def.name) return schema;
  return updateProperty(schema, {
    ...def,
    name: clean,
    keys: def.keys?.length ? def.keys : [def.name],
  });
}

/**
 * Change a property's type. Becoming a select takes the existing values as
 * options, so a text column turns into a select without losing anything.
 */
export function changePropertyType(
  schema: DatabaseSchema,
  id: string,
  type: PropertyType,
  existingValues: unknown[] = [],
): DatabaseSchema {
  const def = schema.properties.find((p) => p.id === id);
  if (!def || def.type === type) return schema;
  let next: PropertyDef = { ...def, type };
  if (SELECT_TYPES.includes(type)) {
    next.options ??= [];
    for (const v of existingValues) for (const name of asList(v)) next = withOption(next, name);
  } else {
    delete next.options;
  }
  let result = updateProperty(schema, next);
  // A board grouped by this property needs a select type.
  if (!['select', 'status'].includes(type)) {
    result = {
      ...result,
      views: result.views.map((v) => (v.groupBy === id ? { ...v, groupBy: undefined } : v)),
    };
  }
  return result;
}

/** Remove a property from the schema and every view (row values are kept). */
export function deleteProperty(schema: DatabaseSchema, id: string): DatabaseSchema {
  return {
    ...schema,
    properties: schema.properties.filter((p) => p.id !== id),
    views: schema.views.map((v) => ({
      ...v,
      filters: v.filters.filter((f) => f.propId !== id),
      sorts: v.sorts.filter((s) => s.propId !== id),
      ...(v.columns ? { columns: v.columns.filter((c) => c !== id) } : {}),
      ...(v.groupBy === id ? { groupBy: undefined } : {}),
    })),
  };
}

export function updateView(schema: DatabaseSchema, view: ViewDef): DatabaseSchema {
  return { ...schema, views: schema.views.map((v) => (v.id === view.id ? view : v)) };
}

/** Show or hide a property in a view. */
export function toggleColumn(
  schema: DatabaseSchema,
  view: ViewDef,
  propId: string,
  visible: boolean,
): DatabaseSchema {
  const current = view.columns ?? schema.properties.map((p) => p.id);
  const columns = visible
    ? current.includes(propId)
      ? current
      : // Keep the schema order for re-shown properties.
        schema.properties.map((p) => p.id).filter((id) => id === propId || current.includes(id))
    : current.filter((c) => c !== propId);
  return updateView(schema, { ...view, columns });
}

export function addView(
  schema: DatabaseSchema,
  name: string,
  type: ViewType,
): { schema: DatabaseSchema; view: ViewDef } {
  const view: ViewDef = { ...defaultView(name, type) };
  if (type === 'board') {
    const group = schema.properties.find((p) => p.type === 'status' || p.type === 'select');
    if (group) view.groupBy = group.id;
  }
  return { view, schema: { ...schema, views: [...schema.views, view] } };
}

export function deleteView(schema: DatabaseSchema, id: string): DatabaseSchema {
  if (schema.views.length <= 1) return schema;
  return { ...schema, views: schema.views.filter((v) => v.id !== id) };
}

/** Move a visible property one place left (-1) or right (+1) in a view. */
export function moveColumn(
  schema: DatabaseSchema,
  view: ViewDef,
  propId: string,
  delta: -1 | 1,
): DatabaseSchema {
  const columns = viewColumns(schema, view).map((p) => p.id);
  const i = columns.indexOf(propId);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= columns.length) return schema;
  [columns[i], columns[j]] = [columns[j]!, columns[i]!];
  return updateView(schema, { ...view, columns });
}

/** Remember a column width (pixels) for a view. */
export function setColumnWidth(
  schema: DatabaseSchema,
  view: ViewDef,
  propId: string,
  width: number,
): DatabaseSchema {
  const w = Math.round(Math.min(800, Math.max(80, width)));
  return updateView(schema, { ...view, widths: { ...view.widths, [propId]: w } });
}

// ---- computed values (backlink, rollup, formula) --------------------------------------------

/** Another database, for relations, backlinks and rollups. */
export interface RelatedDatabase {
  schema: DatabaseSchema;
  rows: RowLike[];
}

const titleKey = (t: string) => lower(t.trim());

/** Titles a link-like value points to ("[[Alpha|A]]" → "alpha"). */
function linkedTitles(v: unknown): string[] {
  return asList(v).map((x) => titleKey(linkTarget(x)));
}

function rollup(
  def: PropertyDef,
  linked: RowLike[],
  target: RelatedDatabase | undefined,
  today: string,
): unknown {
  const r = def.rollup!;
  const tdef = target?.schema.properties.find((p) => p.id === r.target);
  const values = linked.map((row) =>
    r.target === TITLE_ID ? row.title : tdef ? cellValue(tdef, row) : undefined,
  );
  const filled = values.filter((v) => !isEmptyValue(v));
  const numbers = filled.map(Number).filter((n) => !Number.isNaN(n));
  const days = filled.map((v) => splitDateTime(String(v))?.date).filter((d): d is string => !!d);
  const pct = (n: number) => (linked.length ? Math.round((n / linked.length) * 100) : 0);
  switch (r.fn) {
    case 'count':
      return linked.length;
    case 'count_values':
      return filled.length;
    case 'sum':
      return numbers.reduce((a, b) => a + b, 0);
    case 'average':
      return numbers.length
        ? Math.round((numbers.reduce((a, b) => a + b, 0) / numbers.length) * 100) / 100
        : null;
    case 'min':
      return numbers.length ? Math.min(...numbers) : null;
    case 'max':
      return numbers.length ? Math.max(...numbers) : null;
    case 'earliest':
      return days.sort()[0] ?? null;
    case 'latest':
      return days.sort().at(-1) ?? null;
    case 'percent_checked':
      return pct(values.filter((v) => v === true || v === 'true').length);
    case 'percent_matching': {
      if (!target || !r.match) return null;
      const f: Filter = { propId: r.target, ...r.match };
      return pct(linked.filter((row) => matchesFilter(target.schema, row, f, today)).length);
    }
    default:
      return filled.map((v) => textOf(tdef, v));
  }
}

/**
 * Fill `computed` for every row: backlinks first, then rollups (over relations
 * or backlinks), then formulas in schema order (a formula may use earlier ones).
 */
export function computeRows<R extends RowLike>(
  schema: DatabaseSchema,
  rows: R[],
  related: Map<string, RelatedDatabase>,
  today: string,
): (R & { computed?: Record<string, unknown> })[] {
  const byTitle = (db: RelatedDatabase | undefined) => {
    const m = new Map<string, RowLike>();
    for (const r of db?.rows ?? []) if (!m.has(titleKey(r.title))) m.set(titleKey(r.title), r);
    return m;
  };
  const indexes = new Map<string, Map<string, RowLike>>();
  const index = (id: string) => {
    if (!indexes.has(id)) indexes.set(id, byTitle(related.get(id)));
    return indexes.get(id)!;
  };
  const backlinks = schema.properties.filter((p) => p.type === 'backlink' && p.backlink);
  const rollups = schema.properties.filter((p) => p.type === 'rollup' && p.rollup);
  const formulas = schema.properties.filter((p) => p.type === 'formula' && p.formula);
  if (!backlinks.length && !rollups.length && !formulas.length) return rows;

  return rows.map((row) => {
    const computed: Record<string, unknown> = { ...row.computed };
    const me = titleKey(row.title);
    for (const def of backlinks) {
      const db = related.get(def.backlink!.databaseId);
      const via = db?.schema.properties.find((p) => p.id === def.backlink!.propertyId);
      computed[def.id] =
        db && via && me
          ? db.rows
              .filter((r) => linkedTitles(cellValue(via, r)).includes(me))
              .map((r) => `[[${r.title}]]`)
          : [];
    }
    for (const def of rollups) {
      const via = schema.properties.find((p) => p.id === def.rollup!.via);
      const dbId =
        via?.type === 'relation'
          ? via.relation?.databaseId
          : via?.type === 'backlink'
            ? via.backlink?.databaseId
            : undefined;
      const links =
        via?.type === 'backlink' ? computed[via.id] : via ? rawValue(row.props, via) : undefined;
      const idx = dbId ? index(dbId) : new Map<string, RowLike>();
      const linked = linkedTitles(links)
        .map((t) => idx.get(t))
        .filter((r): r is RowLike => !!r);
      computed[def.id] = rollup(def, linked, dbId ? related.get(dbId) : undefined, today);
    }
    for (const def of formulas) {
      const value = runFormula(def.formula!.expression, {
        today,
        prop: (name) => {
          const n = lower(name.trim());
          if (n === 'title' || n === 'naam' || n === 'name') return row.title;
          const p = schema.properties.find((x) => lower(x.name) === n);
          if (!p) throw new FormulaError(`Onbekende eigenschap "${name}"`);
          return isComputed(p) ? computed[p.id] : rawValue(row.props, p);
        },
      });
      computed[def.id] = value instanceof FormulaError ? { error: value.message } : value;
    }
    return { ...row, computed };
  });
}

/** Databases a schema reads from (relations, backlinks). */
export function relatedDatabaseIds(schema: DatabaseSchema): string[] {
  const ids = new Set<string>();
  for (const p of schema.properties) {
    if (p.relation?.databaseId) ids.add(p.relation.databaseId);
    if (p.backlink?.databaseId) ids.add(p.backlink.databaseId);
  }
  return [...ids];
}
